import React, { useState } from 'react';
import '../pages-styles/FAQ.css';
import { usePageMeta } from '../hooks/usePageMeta';

function FAQ() {
  usePageMeta(
    'Frequently Asked Questions | Magbity Agency Ltd',
    'Find answers to common questions about Magbity Agency Ltd, football agency services, player representation, and club services.'
  );

  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    {
      category: 'About Magbity Agency',
      questions: [
        {
          q: 'What is Magbity Agency Ltd?',
          a: 'Magbity Agency Ltd is a professional football agency specializing in player representation, club services, and football business consultancy. We work with integrity, transparency, and a long-term approach to football careers and development projects.'
        },
        {
          q: 'How long has Magbity Agency been operating?',
          a: 'Magbity Agency Ltd is a registered company in England & Wales with a proven track record in football agency services. We combine professional expertise with a commitment to ethical business practices.'
        },
        {
          q: 'Are you licensed by FIFA?',
          a: 'Yes, Magbity Agency Ltd operates with FIFA and FA licensing. We adhere to all regulatory requirements and professional standards in football representation.'
        },
        {
          q: 'What makes Magbity Agency different?',
          a: 'We prioritize long-term player development over quick transactions. Our holistic approach combines representation, consultancy, and strategic planning to maximize career opportunities.'
        }
      ]
    },
    {
      category: 'Player Representation',
      questions: [
        {
          q: 'How can I register as a player with Magbity Agency?',
          a: 'You can contact us through our website contact form or email directly at emagbity@gmail.com. Submit your player profile, career goals, and any relevant media. Our team will review your submission and discuss representation opportunities.'
        },
        {
          q: 'What does player representation include?',
          a: 'Our player representation services include: contract negotiation, transfer management, career planning, club liaison, image rights management, and professional development support.'
        },
        {
          q: 'Do you represent youth players?',
          a: 'Yes, we work with players at various career stages. We have a dedicated youth development program focusing on long-term career growth and education alongside football.'
        },
        {
          q: 'What are your commission rates?',
          a: 'Commission rates vary based on contract complexity, player experience level, and services provided. Contact us directly for personalized rate information and negotiations.'
        },
        {
          q: 'Can you help with international transfers?',
          a: 'Absolutely. We have experience managing international transfers, work permits, visa processes, and cross-border negotiations. Our network extends across Europe and beyond.'
        },
        {
          q: 'How do you support player development?',
          a: 'Beyond contract management, we provide career mentoring, performance analysis, professional development guidance, and connections to training facilities and coaching resources.'
        }
      ]
    },
    {
      category: 'Club Services',
      questions: [
        {
          q: 'What services do you offer to clubs?',
          a: 'Our club services include: player recruitment, scouting, contract management, academy consultation, technical staff placement, and strategic football business planning.'
        },
        {
          q: 'How can a club engage your recruitment services?',
          a: 'Clubs can contact us with their specific player requirements, target positions, budget parameters, and timelines. We then source qualified candidates and manage the entire recruitment process.'
        },
        {
          q: 'Do you provide scouting services?',
          a: 'Yes, we offer comprehensive scouting services including player identification, performance analysis, and market research to help clubs build competitive squads.'
        },
        {
          q: 'Can you help with academy development?',
          a: 'We provide consultation on academy structure, coaching standards, player development programs, and pathways to professional football.'
        }
      ]
    },
    {
      category: 'Football Consultancy',
      questions: [
        {
          q: 'What is football consultancy?',
          a: 'Football consultancy involves providing strategic advice on football business matters including: club operations, revenue optimization, market analysis, project management, and organizational development.'
        },
        {
          q: 'Who can benefit from football consultancy?',
          a: 'Clubs, sports organizations, investors, and football-related businesses can benefit from our consultancy services for strategic planning and operational optimization.'
        },
        {
          q: 'How do consultancy projects work?',
          a: 'We start with a detailed assessment of your specific needs, develop a customized strategy, implement recommendations, and provide ongoing support and monitoring.'
        },
        {
          q: 'What industries do you consult for?',
          a: 'We work across professional clubs, semi-professional organizations, sports management companies, football academies, and football-related business ventures.'
        }
      ]
    },
    {
      category: 'Contact & Working With Us',
      questions: [
        {
          q: 'How do I contact Magbity Agency?',
          a: 'You can reach us via: Website contact form: magbityagencyltd.com/contact'
        },
        {
          q: 'What is your response time?',
          a: 'We aim to respond to all inquiries within 2-3 business days. Urgent matters may receive priority attention.'
        },
        {
          q: 'Do you offer free consultations?',
          a: 'Yes, we offer initial consultations to discuss your needs and how we can help. This helps us understand your requirements and determine the best approach.'
        },
        {
          q: 'What are your office hours?',
          a: 'We operate Monday to Friday, 9 AM to 6 PM (GMT). Emergency matters can be discussed via phone during business hours or email for urgent follow-ups.'
        },
        {
          q: 'Do you work internationally?',
          a: 'Yes, we have an international network and work with players and clubs across Europe and beyond. Language and time zone differences are not barriers to our services.'
        }
      ]
    },
    {
      category: 'Contracts & Legal',
      questions: [
        {
          q: 'What happens after I sign with Magbity Agency?',
          a: 'After signing, you receive a detailed representation agreement outlining terms, commission rates, and services provided. We then begin active representation aligned with your career goals.'
        },
        {
          q: 'Can I terminate my representation agreement?',
          a: 'Termination terms depend on your specific contract. We generally offer flexibility and will discuss exit options if circumstances change significantly.'
        },
        {
          q: 'Are there long-term contracts?',
          a: 'Contracts typically range from 1-3 years with renewal options. Duration depends on the parties and is negotiable during agreement discussions.'
        },
        {
          q: 'How is confidentiality handled?',
          a: 'We treat all client information with strict confidentiality. Sensitive details about negotiations, medical information, and personal matters are protected.'
        }
      ]
    },
    {
      category: 'Privacy & Compliance',
      questions: [
        {
          q: 'How is my personal data protected?',
          a: 'We comply with GDPR and all data protection regulations. Your data is stored securely and only used for agreed purposes. See our Privacy Policy for details.'
        },
        {
          q: 'Will you share my information with third parties?',
          a: 'We only share information with relevant clubs, partners, and service providers necessary to provide our services. We never sell data to third parties. Your consent is always sought for non-essential sharing.'
        },
        {
          q: 'What is your privacy policy?',
          a: 'Our complete Privacy Policy is available at magbityagencyltd.com/privacy-policy and covers data collection, usage, security, and your rights.'
        },
        {
          q: 'How can I request my data?',
          a: 'You have the right to request a copy of all data we hold about you. Contact emagbity@gmail.com with your request, and we will provide this within 30 days.'
        }
      ]
    }
  ];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  let globalIndex = 0;

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="faq-hero-content">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to common questions about Magbity Agency and our services</p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="faq-content">
        <div className="faq-container">
          {faqs.map((section, sectionIdx) => (
            <div key={sectionIdx} className="faq-section">
              <h2 className="faq-section-title">{section.category}</h2>
              
              <div className="faq-list">
                {section.questions.map((item, itemIdx) => {
                  const itemIndex = globalIndex++;
                  return (
                    <div key={itemIdx} className="faq-item">
                      <button
                        className={`faq-question ${activeIndex === itemIndex ? 'active' : ''}`}
                        onClick={() => toggleFAQ(itemIndex)}
                        aria-expanded={activeIndex === itemIndex}
                      >
                        <span className="faq-question-text">{item.q}</span>
                        <span className="faq-toggle-icon">
                          {activeIndex === itemIndex ? '−' : '+'}
                        </span>
                      </button>
                      
                      {activeIndex === itemIndex && (
                        <div className="faq-answer">
                          <p>{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions? */}
        <div className="faq-contact-cta">
          <h2>Didn't find what you're looking for?</h2>
          <p>Our team is here to help. Get in touch with us directly.</p>
          <div className="faq-cta-buttons">
            <a href="/contact" className="cta-btn primary">
              Send Us a Message
            </a>
            <a href="mailto:emagbity@gmail.com" className="cta-btn secondary">
              Email Us
            </a>
            <a href="tel:+447931359923" className="cta-btn tertiary">
              Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FAQ;
